import { Router } from '@angular/router';
import { HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, catchError, delay, finalize, map, of, switchMap, take, tap, timeout } from 'rxjs'
import { AppStateService } from '../store/state/app/app.state.service'
import { UserStateService } from '../store/state/user/user.state.service'
import { DataService } from './data.service'
import { DeviceService } from './device.service'
import { EnvironmentService } from './environment.service'
import { LoginReq, LoginResp, UserWelcomeResponse } from '../models/Login'
import { DiscoveryResponse, SsoExchangeRequest } from '../models/Discovery'
import { CreatePasswordReq } from '../models/CreatePassword'
import { ForgotPasswordResp, ForgotPasswordReq } from '../models/ForgotPassword'
import { SessionTypeEnum } from '../models/SessionTypeEnum'
import { ValidatePasscodeResp, ValidatePasscodeReq } from '../models/ValidatePasscode'
import * as uuid from 'uuid'
import { Device } from '../models/Device';
import { Session, Token } from '../models/Gateway';
import { AppEventService, EVENT_TYPE } from './app-event.service';
import { LoginFlowStateService } from '../store/state/login-flow/login-flow.state.service';
import { OrderStateServiceWrapper } from '../store/state/order/order.state.wrapper.service';
import { AnalyticsService } from './analytics/analytics.service';
import { NotificationService } from './notification.service';
import { Message } from '../enums/message.enum';
import { SentryService } from "./sentry.service";
import { TokenUtil } from '../util/token.util';
import { OrderRoutes } from '../../features/orders/orders-routes.enum';
import { FeatureFlagService } from './feature-flag.service';
export const SESSION_EXPIRED_KEY = 'sessionExpiredInactivity';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  #logoutInFlight = false
  #environmentService = inject(EnvironmentService)
  #dataService = inject(DataService)
  #deviceService = inject(DeviceService)
  #appStateService = inject(AppStateService)
  #userStateService = inject(UserStateService)
  #loginFlowStateService = inject(LoginFlowStateService)
  orderStateService = inject(OrderStateServiceWrapper)
  #eventService: AppEventService = inject(AppEventService)
  #notificationService = inject(NotificationService)
  analyticsService = inject(AnalyticsService);

  #router = inject(Router)
  #sentryService = inject(SentryService)
  #featureFlagService = inject(FeatureFlagService)
  #tokenUrl = this.#environmentService.getUrl('oauth/token')
  #sessionUrl = this.#environmentService.getUrl('ng/v1/common/startSession')
  #loginUrl = this.#environmentService.getUrl('ng/v1/user/login/')
  #discoveryUrl = this.#environmentService.getUrl('ng/v1/user/discovery/')
  #ssoExchangeUrl = this.#environmentService.getUrl('ng/v1/user/sso/exchange/')
  #forgotPasswordUrl = this.#environmentService.getUrl('ng/v1/user/validate/')
  #validatePinUrl = this.#environmentService.getUrl('ng/v1/user/validatepin/')
  #userUpdateUrl = this.#environmentService.getUrl('ng/v1/user/update/')
  #logoutUrl = this.#environmentService.getUrl('ng/v1/user/logout/')
  #userWelcomeUrl = this.#environmentService.getUrl('ng/v1/user/welcome/')
  #featureTogglesUrl = this.#environmentService.getUrl('featuretoggle/mine/')
  #globalFeatureTogglesUrl = this.#environmentService.getUrl('featuretoggle/global')
  #updateSessionUrl = this.#environmentService.getUrl('ng/v1/common/updateSession/')
  
  restorePersistedSession(): void {
    const session = this.#appStateService.getSession()
    const hasLogin = this.#userStateService.getUser() || this.#loginFlowStateService.getLoginFlow()
    if (!session && !hasLogin && !this.#userStateService.getIsLoggedIn()) return

    const expiresAt = session?.EndDateTime ? Date.parse(session.EndDateTime) : NaN
    if (session?.SessionID && session.ClientAesKey && Number.isFinite(expiresAt) && expiresAt > Date.now()) return

    // An expired browser-restored session goes straight to login. The inactivity
    // message is reserved for a session that expires while the app is running.
    this.resetState()
  }

  public async initDevice(skipLocationRequest: boolean = false) {
    return await this.#deviceService.initDevice(skipLocationRequest)
  }

  initAuthInformationAndLogin(value: { email: string; pass: string; rememberMe: boolean, byPassLocation?: boolean }): Observable<UserWelcomeResponse> {
    return this.fetchToken()
      .pipe(
        delay(500),
        switchMap(() => this.fetchSession()),
        tap((resp) => {
          this.#appStateService.setSession(resp);
        }),
        switchMap(() => this.login(value))
      )
  }

  initAuthInformationAndDiscovery(email: string): Observable<DiscoveryResponse> {
    return this.fetchToken()
      .pipe(
        delay(500),
        switchMap(() => this.fetchSession()),
        tap((resp) => {
          this.#appStateService.setSession(resp);
        }),
        switchMap(() => this.discovery(email))
      )
  }

  discovery(email: string): Observable<DiscoveryResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const endpoint = this.#discoveryUrl + sessionId
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#deviceService.getHashForDeviceId(uuid.v4(), email)
      .pipe(switchMap((deviceId) => {
        const payload = { email, deviceId }
        return this.#dataService.post<typeof payload, DiscoveryResponse>(endpoint, payload, reqHeaders, true)
      }))
  }

  fetchToken(): Observable<Token> {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': `Basic ${this.#environmentService.env.tokenCredentials}`
    })
    return this.#dataService.get<Token>(this.#tokenUrl, headers, true, 'json').pipe(take(1), tap((token: Token) => {
      if (token.issued_at && token.expires_in) {
        token = TokenUtil.fillToken(token);
      }
      this.#appStateService.setToken(token)
    }
    ));
  }

  fetchSession(): Observable<Session> {
    return this.#dataService.get<Session>(this.#sessionUrl)
  }

  updateSession() {
    const sessionId = this.#appStateService.getSession()?.SessionID
    this.#dataService.post<unknown, Session>(this.#updateSessionUrl + sessionId).subscribe({
      next: (resp) => this.#appStateService.setSession(resp),
      error: () => { } // Explicitly swallow error because it will be logged with sentry using http interceptor
    });
  }

  processSAML() {

  }

  login(formData: { email: string; pass: string; rememberMe: boolean, byPassLocation?: boolean }): Observable<UserWelcomeResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID

    const device = this.#appStateService.getDevice()
    if (!device) {
      return of({} as UserWelcomeResponse)
    }

    return this.#deviceService.getHashForDeviceId(uuid.v4(), formData.email)
      .pipe(switchMap((deviceIdHash) => {
        const payload = this.#buildLoginPayload(formData, device, deviceIdHash)
        const endpoint = this.#loginUrl + sessionId
        const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
        return this.#dataService.post<LoginReq, LoginResp>(endpoint, payload, reqHeaders, true)
      }), switchMap((resp) => {
        const authorized = this.#isAuthorized(resp)
        this.#loginFlowStateService.setLoginFlow(resp.LoginFlow)
        this.#userStateService.setUser(resp.UserDetails)
        this.analyticsService.setUser(sessionId, resp);
        this.setSentryUser(authorized)
        return !authorized ? of(null) : this.getWelcomeInfo()
      }),
        map(val => {
          if (val) {
            this.#eventService.raiseEventOf<UserWelcomeResponse>(EVENT_TYPE.PROFILE, val)
          }
          return val
        }))
  }


  ssoLogin(accessToken: string, email: string, byPassLocation: boolean): Observable<UserWelcomeResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID

    const device = this.#appStateService.getDevice()
    if (!device) {
      return of({} as UserWelcomeResponse)
    }

    return this.#deviceService.getHashForDeviceId(uuid.v4(), email)
      .pipe(switchMap((deviceIdHash) => {
        const payload = this.#buildSsoPayload(accessToken, device, deviceIdHash, byPassLocation)
        const endpoint = this.#ssoExchangeUrl + sessionId
        const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
        return this.#dataService.post<SsoExchangeRequest, LoginResp>(endpoint, payload, reqHeaders, true)
      }), switchMap((resp) => {
        const authorized = this.#isAuthorized(resp)
        this.#loginFlowStateService.setLoginFlow(resp.LoginFlow)
        this.#userStateService.setUser(resp.UserDetails)
        this.analyticsService.setUser(sessionId, resp);
        this.setSentryUser(authorized)
        return !authorized ? of(null) : this.getWelcomeInfo()
      }),
        map(val => {
          if (val) {
            this.#eventService.raiseEventOf<UserWelcomeResponse>(EVENT_TYPE.PROFILE, val)
          }
          return val
        }))
  }

  getHomeRoute(): string {
    const isTpvAgent = this.#userStateService.getUserWelcomeInfo()?.Modules?.some(x => x.toLowerCase() === 'tpv')
    return isTpvAgent ? '/tpv' : OrderRoutes.WELCOME
  }

  getWelcomeInfo(): Observable<UserWelcomeResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID;
    const endpoint = this.#userWelcomeUrl + sessionId;
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.get<UserWelcomeResponse>(endpoint, reqHeaders)
      .pipe(tap((resp) => {
        this.#userStateService.setUserWelcome(resp)
        this.#userStateService.setAuthorized(true)
      }),
        // Login: the user's feature toggles are in place before the first page opens.
        switchMap(resp => this.loadFeatureToggles().pipe(map(() => resp))))
  }

  /**
   * Reads the global feature toggles (Gateway featuretoggle/global, no sign-in needed) when the app starts. Waits at
   * most 3 seconds; a failure keeps the last values (or the built-in flags) and never errors.
   */
  loadGlobalFeatureToggles(): Observable<void> {
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.get<{ [key: string]: boolean }>(this.#globalFeatureTogglesUrl, reqHeaders, true, 'json').pipe(
      timeout(3000),
      tap(toggles => this.#featureFlagService.setGlobalToggles(toggles)),
      map(() => undefined),
      catchError(error => {
        console.warn('Global feature toggles could not be loaded; keeping the current values.', error)
        return of(undefined)
      }))
  }

  /**
   * Reads the signed-in user's feature toggles (Gateway featuretoggle/mine: global combined with their connection and
   * user overrides) and applies them over the global values. Called at login and before every Start New Order.
   * A failure keeps the current values and never errors.
   */
  loadFeatureToggles(): Observable<void> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    if (!sessionId) return of(undefined)
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.get<{ [key: string]: boolean }>(this.#featureTogglesUrl + sessionId, reqHeaders, true).pipe(
      tap(toggles => this.#featureFlagService.setUserToggles(toggles)),
      map(() => undefined),
      catchError(error => {
        console.warn('Feature toggles could not be loaded; keeping the current values.', error)
        return of(undefined)
      }))
  }

  saveBasicInformation(formData: { firstName: string; lastName: string, securityQuestion: number, securityAnswer: string }) {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const userId = this.#userStateService.getUnauthorizedUser()?.UserId

    const payload = {
      UserId: userId,
      FirstName: formData.firstName?.trim(),
      LastName: formData.lastName?.trim(),
      SecurityQuestionId: formData.securityQuestion,
      SecurityQuestionAnswer: formData.securityAnswer,
    }

    const endpoint = this.#userUpdateUrl + sessionId

    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.put<unknown, unknown>(endpoint, payload, reqHeaders)
  }

  initForgotPassword(formData: { email: string }) {
    return this.fetchToken()
      .pipe(
        tap((token: Token) => {
          this.#appStateService.setToken(token)
        }),
        delay(500),
        switchMap(() => {
          return this.fetchSession()
        }),
        switchMap((resp) => {
          this.#appStateService.setSession(resp)
          return this.forgotPassword(formData)
        })
      )
  }

  forgotPassword(formData: { email: string }): Observable<ForgotPasswordResp> {
    const sessionId = this.#appStateService.getSession()?.SessionID

    const payload: ForgotPasswordReq = {
      Email: formData.email,
      SessionType: SessionTypeEnum.FORGOT_PASSWORD
    }

    const endpoint = this.#forgotPasswordUrl + sessionId

    const reqHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('skip', 'true') // Skip the interceptor to prevent misleading toast
    return this.#dataService.post<ForgotPasswordReq, ForgotPasswordResp>(endpoint, payload, reqHeaders)
  }

  validatePasscode(formData: { pass: number }): Observable<ValidatePasscodeResp> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const userId = this.#userStateService.getUnauthorizedUser()?.UserId

    const payload: ValidatePasscodeReq = {
      UserId: userId,
      Pin: formData.pass,
      SessionType: SessionTypeEnum.FORGOT_PASSWORD
    }

    const endpoint = this.#validatePinUrl + sessionId

    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.post<ValidatePasscodeReq, ValidatePasscodeResp>(endpoint, payload, reqHeaders)
  }

  createPassword(formData: { pass: string }, SessionType = SessionTypeEnum.FORGOT_PASSWORD): Observable<unknown> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const userId = this.#userStateService.getUnauthorizedUser()?.UserId

    const payload: CreatePasswordReq = {
      UserId: userId,
      Password: formData.pass,
      SessionType
    }

    const endpoint = this.#userUpdateUrl + sessionId

    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.put<CreatePasswordReq, unknown>(endpoint, payload, reqHeaders)
  }


  logout(sessionExpired = false) {
    if (this.#logoutInFlight) {
      return of(null)
    }
    this.#logoutInFlight = true
    const sessionId = this.#appStateService.getSession()?.SessionID
    if (!sessionId) {
      this.#completeLogout(sessionExpired)
      return of(null)
    }
    const endpoint = this.#logoutUrl + sessionId
    return this.#dataService.post(endpoint, undefined, undefined, true).
      pipe(finalize(() => this.#completeLogout(sessionExpired)));
  }

  // navigation and the toast must live here behind #logoutInFlight, not in the component.
  #completeLogout(sessionExpired: boolean) {
    this.resetState()
    if (sessionExpired) {
      sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true')
    } else {
      this.#notificationService.showSuccess(Message.LogoutSuccess)
    }
    this.#router.navigate(['/login']).finally(() => { this.#logoutInFlight = false })
  }

  #buildLoginPayload(formData: { email: string; pass: string; rememberMe: boolean, byPassLocation?: boolean }, device: Device, deviceIdHash: string) {
    return {
      Email: formData.email,
      Password: formData.pass,
      DeviceID: deviceIdHash,
      Latitude: device.latitude,
      Longitude: device.longitude,
      BrowserType: device.browserName,
      BrowserPlatform: device.platform,
      BrowserIsMobile: device.browserIsMobile,
      BrowserMobileModel: device.browserMobileModel,
      BrowserUserAgent: device.userAgent,
      GeoAccuracy: device.geoAccuracy,
      LocationServicesOptional: formData.byPassLocation ? true : false
    }
  }

  #buildSsoPayload(accessToken: string, device: Device, deviceIdHash: string, byPassLocation: boolean): SsoExchangeRequest {
    return {
      AccessToken: accessToken,
      DeviceID: deviceIdHash,
      Latitude: device.latitude,
      Longitude: device.longitude,
      BrowserType: device.browserName,
      BrowserPlatform: device.platform,
      BrowserIsMobile: device.browserIsMobile,
      BrowserMobileModel: device.browserMobileModel,
      BrowserUserAgent: device.userAgent,
      GeoAccuracy: device.geoAccuracy,
      AppVersion: this.#environmentService.env.appVersion,
      LocationServicesOptional: byPassLocation ? true : false
    }
  }

  resetState() {
    this.#featureFlagService.setUserToggles(null);
    this.#userStateService.reset();
    this.#appStateService.resetGatewayAction();
    this.#loginFlowStateService.setLoginFlow(null);
    this.orderStateService.reset();
    sessionStorage.clear();
    localStorage.clear();
  }

  #isAuthorized(resp: LoginResp): boolean {
    return !(resp.LoginFlow.Do2FaCheck || resp.LoginFlow.Do2FaSetup || resp.LoginFlow.DoForcedPasswordReset || resp.LoginFlow.DoSecurityQandA)
  }

  refreshSession() {
    // Always fetch token but update session only if expiring within 30 minutes
    this.fetchToken().pipe(take(1)).subscribe(() => {
      const session: Session = this.#appStateService.getSession();
      if (!session?.EndDateTime) {
        return;
      }

      const endTimeUtc = new Date(session.EndDateTime);
      const diffInMinutes = (endTimeUtc.getTime() - Date.now()) / (1000 * 60);

      // Update only if session is still valid and expiring soon
      if (diffInMinutes > 0 && diffInMinutes <= 30) {
        this.updateSession();
      }
    });
  }

  setSentryUser(authorized: boolean) {
    if (authorized) {
      this.#sentryService.setUser({
        id: this.#userStateService.getUser()?.UserId, email: this.#userStateService.getUser()?.LoginEmail,
        username: this.#userStateService.getUser()?.FirstName + ' ' + this.#userStateService.getUser()?.LastName
      })
    } else {
      this.#sentryService.setUser({ id: this.#userStateService.getUnauthorizedUser()?.UserId, email: this.#userStateService.getUnauthorizedUser()?.Email })
    }
  }

}
